import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import "../components/sudanLanding.scss";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import HeroVisual from "../assets/banner-who-its-for.png";
import { LanguageProvider, useLanguage } from "../i18n/LanguageContext";

const AUDIENCES = [
   {
      label: "Sudanese NGOs & CSOs",
      title: "Lead your own development",
      paragraphs: [
         "You already have the knowledge of your communities. You know what they need, how to reach them, and how to deliver. KAPorg gives you the tools to demonstrate that knowledge to the world — and the credentials to receive funding directly, on your own terms.",
         "Whether you are just starting your capacity development journey or already engaging with international donors, KAPorg meets you where you are and takes you where you need to go."
      ],
      bullets: [
         "Free self-assessment platform across five core capacity areas",
         "Tailored action plans linked directly to your assessment results",
         "Curated learning resources and templates aligned to donor standards",
         "KAPorg Certificate of Organizational Capacity",
         "Access to donors and international partners"
      ],
      ctas: [
         { label: "Register for free", to: "/auth/registration", primary: true },
         { label: "Learn about the program", to: "/capacity-accelerator-program", primary: false }
      ],
      cards: [
         {
            title: "Understand where you stand",
            text: "A validated self-assessment that gives you an honest, structured picture of your organization's capacity across the areas donors care about most."
         },
         {
            title: "Build with a plan",
            text: "Turn your assessment results into a concrete improvement plan with tasks, owners, deadlines, and resources — all tracked in one place."
         },
         {
            title: "Prove it to the world",
            text: "A KAPorg certificate gives international donors the evidence they need to fund you directly — without duplicating due diligence processes you have already completed."
         }
      ]
   },
   {
      label: "Donors & International Partners",
      title: "Fund with confidence",
      paragraphs: [
         "The Humanitarian Reset sets a clear direction: more funding, more directly, to local organizations. KAPorg gives you the evidence base to act on that commitment — without duplicating due diligence or burdening your partners with additional assessment processes.",
         "KAPorg provides verified, standardized evidence of organizational readiness across all 18 states of Sudan — making it faster and easier to identify capable local partners and channel funding where it is needed most."
      ],
      bullets: [
         "Verified capacity assessments aligned to UN HACT, EU PADOR, and USAID standards",
         "Transparent partner profiles across all 18 Sudanese states",
         "Aligned with IASC Reset localization targets",
         "Significantly reduced due diligence burden",
         "Real-time portfolio view of partner capacity and progress"
      ],
      ctas: [{ label: "Learn more", to: "/humanitarian-reset", primary: true }],
      cards: [
         {
            title: "Meet your Grand Bargain commitments",
            text: "KAPorg gives you a structured, verified pathway to increase direct funding to local organizations — in line with the IASC Humanitarian Reset's 70% aspiration."
         },
         {
            title: "Reduce due diligence burden",
            text: "KAPorg assessments are aligned with the standards you already use. Your partners complete the process once and you access the results directly."
         },
         {
            title: "Identify the right partners faster",
            text: "A real-time view of partner capacity across Sudan means you spend less time searching and more time funding."
         }
      ]
   },
   {
      label: "Capacity Development Experts",
      title: "Extend your reach",
      paragraphs: [
         "Connect with motivated Sudanese organizations that need your expertise. KAPorg gives you a structured framework to mentor, train, and guide organizations through the assessment and improvement process — while contributing directly to the localization agenda.",
         "The platform handles the structure. You bring the expertise. Together, you give Sudanese organizations the best possible chance of accessing direct international funding."
      ],
      bullets: [
         "Access to motivated national organizations across 18 states",
         "Structured mentorship and facilitation framework",
         "Contribute directly to the localization agenda",
         "Build lasting relationships within the Sudanese humanitarian sector",
         "Participate in the KAPorg Capacity Accelerator Program as a facilitator"
      ],
      ctas: [{ label: "Learn more", to: "/capacity-accelerator-program", primary: true }],
      cards: [
         {
            title: "Work with organizations that are ready",
            text: "KAPorg organizations have already completed a self-assessment and built an action plan. When you arrive, the groundwork is done."
         },
         {
            title: "A framework that scales your impact",
            text: "The structured pathway means your expertise goes further — you are not starting from scratch with each organization."
         },
         {
            title: "Be part of something larger",
            text: "The Humanitarian Reset is the most significant shift in humanitarian funding in a generation. KAPorg puts you at the center of it."
         }
      ]
   }
];

const WhoItsForInner = () => {
   const { t, dir, lang } = useLanguage();

   useEffect(() => {
      window.scrollTo(0, 0);
   }, []);

   return (
      <div className="App">
         <div
            className={`sudan-kaporg sudan-landing ${lang === "ar" ? "sudan-rtl" : ""}`}
            dir={dir}
         >
            <Navbar />

            {/* Hero */}
            <section className="sd-page-hero">
               <div className="container">
                  <div className="sd-page-hero-inner">
                     <div className="sd-page-hero-text">
                        <span className="sd-chip">{t("Who it's for")}</span>
                        <h1>{t("Built for Local Organizations, Donors, and Experts")}</h1>
                        <p>
                           {t(
                              "Everyone in the humanitarian ecosystem is working toward the same goal. KAPorg gives each of them the tools to get there faster."
                           )}
                        </p>
                        <div className="sd-page-hero-cta">
                           <Link to="/auth/registration" className="sd-btn sd-btn-accent">
                              {t("Register for free")}
                           </Link>
                           <Link
                              to="/capacity-accelerator-program"
                              className="sd-btn sd-btn-ghost-light"
                           >
                              {t("Capacity Accelerator Program")}
                           </Link>
                        </div>
                     </div>
                     <div className="sd-page-hero-visual">
                        <img src={HeroVisual} alt="" aria-hidden="true" />
                     </div>
                  </div>
               </div>
            </section>

            {AUDIENCES.map((audience, index) => (
               <section
                  key={audience.label}
                  className={`sd-section ${index % 2 === 0 ? "sd-bg-white" : "sd-bg-light"}`}
               >
                  <div className="container">
                     <div className={`sd-split ${index % 2 === 1 ? "reverse" : ""}`}>
                        <div className="sd-split-main">
                           <span className="sd-audience-label">{t(audience.label)}</span>
                           <h2 className="sd-block-title">{t(audience.title)}</h2>
                           {audience.paragraphs.map((para, i) => (
                              <p className="sd-block-text" key={i}>
                                 {t(para)}
                              </p>
                           ))}
                           <ul className="sd-tick-list" style={{ margin: "24px 0" }}>
                              {audience.bullets.map((bullet) => (
                                 <li key={bullet}>{t(bullet)}</li>
                              ))}
                           </ul>
                           <div style={{ display: "flex", gap: "16px", flexWrap: "wrap" }}>
                              {audience.ctas.map((cta) => (
                                 <Link
                                    key={cta.label}
                                    to={cta.to}
                                    className={`sd-btn ${
                                       cta.primary ? "sd-btn-primary" : "sd-btn-secondary"
                                    }`}
                                 >
                                    {t(cta.label)}
                                 </Link>
                              ))}
                           </div>
                        </div>
                        <div className="sd-split-aside">
                           <div className="sd-card-grid" style={{ gridTemplateColumns: "1fr" }}>
                              {audience.cards.map((card) => (
                                 <div className="sd-card" key={card.title}>
                                    <h4>{t(card.title)}</h4>
                                    <p>{t(card.text)}</p>
                                 </div>
                              ))}
                           </div>
                        </div>
                     </div>
                  </div>
               </section>
            ))}

            {/* Closing */}
            <section className="sd-section sd-closing">
               <div className="container">
                  <div className="sd-closing-inner">
                     <h2>{t("Find your place in the KAPorg ecosystem.")}</h2>
                     <p>
                        {t(
                           "Whether you are building, funding, or supporting — there is a role for you on KAPorg Sudan."
                        )}
                     </p>
                     <div className="sd-closing-cta">
                        <Link to="/auth/registration" className="sd-btn sd-btn-accent">
                           {t("Register for free")}
                        </Link>
                        <Link
                           to="/capacity-accelerator-program"
                           className="sd-btn sd-btn-ghost-light"
                        >
                           {t("Capacity Accelerator Program")}
                        </Link>
                     </div>
                  </div>
               </div>
            </section>

            <Footer />
         </div>
      </div>
   );
};

const WhoItsForPage = () => (
   <LanguageProvider>
      <WhoItsForInner />
   </LanguageProvider>
);

export default WhoItsForPage;
